import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const OLIVE = '#3d4128';
const SAGE = '#b9c096';
const CREAM = '#fbf8f3';
const INK = '#1f2937';
const MUTE = '#64748b';
const ACCENT_BG = '#eef2e4';

function fmt(n) {
  if (n === null || n === undefined) return '\u2014';
  return Number(n).toLocaleString();
}

function fmtK(n) {
  if (n === null || n === undefined) return '\u2014';
  const v = Number(n);
  return v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toLocaleString();
}

function Delta({ change, goodUp = true }) {
  if (change === null || change === undefined || change === 0) {
    return <span style={{ fontSize: 12, color: MUTE }}>no change</span>;
  }
  const up = change > 0;
  const good = goodUp ? up : !up;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: good ? '#166534' : '#991b1b' }}>
      {up ? '\u25b2 +' : '\u25bc '}{Math.abs(change).toLocaleString()} vs last month
    </span>
  );
}

const STATUS_LABEL = {
  top_3: 'Holding top 3', page_1: 'On page 1', near_page_1: 'Near page 1',
  page_2_5: 'Building', deep: 'Early stage', no_data: '\u2014',
};

export default function CombinedReport({ websiteId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workText, setWorkText] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const load = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getCombinedReport(websiteId);
      setReport(data);
      setWorkText(data.work_completed || '');
    } catch (e) {
      setError(e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message);
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  const saveNote = async () => {
    if (!report) return;
    setSaving(true);
    setSavedMsg('');
    try {
      await api.saveReportNote(websiteId, report.year, report.month, workText);
      setSavedMsg('Saved');
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      setSavedMsg('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!websiteId) return <div style={{ padding: 40, color: MUTE }}>Select a website to see its report.</div>;
  if (loading && !report) return <div style={{ padding: 40, color: MUTE }}>Building the report{'\u2026'}</div>;
  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ color: '#991b1b', marginBottom: 12 }}>Could not build the report: {error}</div>
        <button onClick={load} style={{ padding: '8px 18px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }
  if (!report) return null;

  const b = report.business || { available: false };
  const vis = report.visibility || {};
  const money = report.money_keywords || [];
  const priorities = report.next_month_priorities || [];

  const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' };
  const label = { fontSize: 13, color: MUTE, marginBottom: 4 };
  const big = { fontSize: 24, fontWeight: 800, color: INK, lineHeight: 1 };

  return (
    <div>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #combined-report, #combined-report * { visibility: visible; }
          #combined-report { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          nav, header { display: none !important; }
        }
      `}</style>

      <div id="combined-report" style={{ padding: '20px 24px', maxWidth: 940 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid ' + INK, paddingBottom: 14, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.5px', color: OLIVE, textTransform: 'uppercase' }}>{report.brand || 'Paramount Tech Network'}</div>
            <h1 style={{ margin: '4px 0 2px', fontSize: 24, fontWeight: 800, color: INK }}>Performance report</h1>
            <div style={{ fontSize: 13, color: MUTE }}>{report.domain} {'\u00b7'} {report.report_month}</div>
          </div>
          <button className="no-print" onClick={() => window.print()} style={{ padding: '9px 18px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Download PDF</button>
        </div>

        {/* Outcome hero */}
        <div style={{ background: CREAM, border: '1px solid ' + SAGE, borderRadius: 12, padding: '18px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: OLIVE, lineHeight: 1.5 }}>{report.headline_verdict}</div>
          <div style={{ fontSize: 12.5, color: MUTE, marginTop: 4 }}>Compared with {report.comparison_month}.</div>
        </div>

        {/* Business metrics */}
        {b.available && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 12, marginBottom: 14 }}>
              <div style={card}>
                <div style={label}>Unique visitors</div>
                <div style={big}>{fmt(b.visitors.value)}</div>
                <div style={{ fontSize: 12, color: MUTE }}>{fmt(b.sessions ? b.sessions.value : null)} sessions</div>
              </div>
              <div style={card}>
                <div style={label}>Leads captured</div>
                <div style={big}>{fmt(b.leads.value)}</div>
                <Delta change={b.leads.change} />
              </div>
              <div style={card}>
                <div style={label}>Booked</div>
                <div style={big}>{fmt(b.booked.value)}</div>
                <Delta change={b.booked.change} />
              </div>
              <div style={card}>
                <div style={label}>Phone calls</div>
                <div style={big}>{fmt(b.calls ? b.calls.value : 0)}</div>
                <div style={{ fontSize: 12, color: MUTE }}>tracked this month</div>
              </div>
            </div>

            {/* Channel breakdown */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
              <div style={{ fontSize: 13, color: MUTE, marginBottom: 12 }}>How prospects reached the firm</div>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                <div><span style={{ fontSize: 22, fontWeight: 800, color: INK }}>{fmt(b.leads.value)}</span> <span style={{ fontSize: 13, color: MUTE }}>web forms</span></div>
                <div><span style={{ fontSize: 22, fontWeight: 800, color: INK }}>{fmt(b.calls ? b.calls.value : 0)}</span> <span style={{ fontSize: 13, color: MUTE }}>phone calls</span></div>
                <div><span style={{ fontSize: 22, fontWeight: 800, color: INK }}>{fmt(b.conversations.value)}</span> <span style={{ fontSize: 13, color: MUTE }}>AI chats</span></div>
                <div><span style={{ fontSize: 22, fontWeight: 800, color: INK }}>{fmt(b.email_clicks ? b.email_clicks.value : 0)}</span> <span style={{ fontSize: 13, color: MUTE }}>email clicks</span></div>
              </div>
            </div>
          </>
        )}

        {/* SEO section */}
        <div style={{ borderTop: '2px solid ' + INK, paddingTop: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: INK }}>Search engine performance</div>
          <div style={{ fontSize: 13, color: MUTE }}>The SEO work driving your visibility in Google</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px,1fr))', gap: 12, marginBottom: 18 }}>
          <div style={card}><div style={{ ...label, fontSize: 12 }}>Clicks from Google</div><div style={{ ...big, fontSize: 22 }}>{fmt(vis.clicks && vis.clicks.value)}</div><Delta change={vis.clicks && vis.clicks.change} /></div>
          <div style={card}><div style={{ ...label, fontSize: 12 }}>On page 1</div><div style={{ ...big, fontSize: 22 }}>{fmt(vis.page_1 && vis.page_1.value)}</div><Delta change={vis.page_1 && vis.page_1.change} /></div>
          <div style={card}><div style={{ ...label, fontSize: 12 }}>In top 3</div><div style={{ ...big, fontSize: 22 }}>{fmt(vis.top_3 && vis.top_3.value)}</div><Delta change={vis.top_3 && vis.top_3.change} /></div>
          <div style={card}><div style={{ ...label, fontSize: 12 }}>Impressions</div><div style={{ ...big, fontSize: 22 }}>{fmtK(vis.impressions && vis.impressions.value)}</div><Delta change={vis.impressions && vis.impressions.change} /></div>
        </div>

        {/* Money keywords */}
        <div style={{ fontSize: 14, fontWeight: 700, color: INK, marginBottom: 4 }}>Your highest-value searches</div>
        <div style={{ fontSize: 12, color: MUTE, marginBottom: 10 }}>The commercial terms that bring in cases {'\u2014'} where they rank now</div>
        <div style={{ marginBottom: 22 }}>
          {money.slice(0, 8).map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '0.5px solid #e2e8f0' }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, color: INK }}>{m.keyword}</span>
                {m.variant_count > 0 && <span style={{ fontSize: 12, color: MUTE }}> +{m.variant_count} variants</span>}
                {m.strategic_note && <div style={{ fontSize: 12, color: MUTE, marginTop: 2, lineHeight: 1.4 }}>{m.strategic_note}</div>}
              </div>
              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: 12, color: OLIVE, background: ACCENT_BG, padding: '2px 8px', borderRadius: 6 }}>Position {m.position != null ? Math.round(m.position) : '\u2014'}</span>
                <div style={{ fontSize: 11, color: MUTE, marginTop: 3 }}>{STATUS_LABEL[m.status] || ''}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Priorities */}
        <div style={{ fontSize: 14, fontWeight: 700, color: INK, marginBottom: 10 }}>Priorities for next month</div>
        <div style={{ marginBottom: 22 }}>
          {priorities.slice(0, 6).map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0' }}>
              <span style={{ fontSize: 12, color: INK, background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, height: 'fit-content' }}>{i + 1}</span>
              <div>
                <span style={{ fontSize: 14, color: INK }}>{p.title}</span>
                {p.covers_keywords > 1 && <span style={{ fontSize: 12, color: MUTE }}> covers {p.covers_keywords} keywords</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Work completed — editable */}
        <div style={{ background: CREAM, border: '1px solid ' + SAGE, borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: OLIVE, fontWeight: 700, marginBottom: 8 }}>Work completed this month</div>
          <textarea
            className="no-print"
            value={workText}
            onChange={(e) => setWorkText(e.target.value)}
            placeholder={'List the SEO and content work delivered this month\u2026'}
            style={{ width: '100%', minHeight: 90, border: '1px solid ' + SAGE, borderRadius: 8, padding: 10, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
          />
          <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <button onClick={saveNote} disabled={saving} style={{ padding: '7px 16px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving\u2026' : 'Save'}</button>
            {savedMsg && <span style={{ fontSize: 13, color: savedMsg === 'Saved' ? '#166534' : '#991b1b' }}>{savedMsg}</span>}
          </div>
          {/* Print view of the saved text */}
          <div style={{ display: 'none', fontSize: 13, color: INK, whiteSpace: 'pre-wrap' }} className="print-only">{workText}</div>
        </div>

        <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6, borderTop: '0.5px solid #e2e8f0', paddingTop: 12 }}>
          Prepared by {report.brand || 'Paramount Tech Network'}. Visitor and session counts exclude bot and internal traffic. Search data from Google Search Console, brand searches excluded. Calls, forms, chats, and bookings reflect tracked activity for the month.
        </div>
      </div>
    </div>
  );
}
